import { type MouseEvent } from 'react';
import Image, { getImageProps } from 'next/image';

import LinkButton from '_components/UI/LinkButton';
import { Breakpoints } from '@hooks/useBreakpoint';
import { isModifiedClick } from '_utils/GA4/clickTracking';
import { trackPromoTileClick } from '_utils/GA4/trackPromoTilesEvent';
import { useLocale } from '@hooks/useLocale';
import { localizeHref } from '@utils/localizeHref';

import type { PromoTile, PromoTileImage } from '../types';

import styles from './PromoTileCard.module.scss';

export interface PromoTileCardProps {
  tile: PromoTile;
  title: string;
  headingTag: 'h2' | 'h3';
  tileCount: number;
  sizes: string;
  interactionLocation: string;
  index: number;
}

// Art direction through one `<picture>` so the browser downloads only the
// crop whose `<source media>` matches, instead of two `<Image>`s hidden by CSS.
const PromoTilePicture = ({
  image,
  mobileImage,
  sizes,
}: {
  image: PromoTileImage;
  mobileImage: PromoTileImage;
  sizes: string;
}) => {
  const {
    props: { srcSet: desktopSrcSet, sizes: desktopSizes },
  } = getImageProps({ src: image.url, alt: image.alt, fill: true, sizes });
  const { props: mobileImageProps } = getImageProps({
    src: mobileImage.url,
    // `<picture>` exposes a single alt whichever source wins.
    alt: image.alt || mobileImage.alt,
    fill: true,
    sizes,
    className: styles.image,
  });

  return (
    <picture>
      <source
        media={`(min-width: ${Breakpoints.tabletXl}px)`}
        srcSet={desktopSrcSet}
        sizes={desktopSizes}
      />
      <img {...mobileImageProps} />
    </picture>
  );
};

const PromoTileCard = ({
  tile,
  title,
  headingTag,
  tileCount,
  sizes,
  interactionLocation,
  index,
}: PromoTileCardProps) => {
  const locale = useLocale();
  const Heading = headingTag;
  const hasOverlay = Boolean(tile.heading || tile.body || tile.ctas.length);

  const handleCtaClick = (
    href: string,
    event: MouseEvent<HTMLAnchorElement>
  ) => {
    const data = {
      interactionLocation,
      linkUrl: href,
      index,
      heading: title ?? '',
      carouselSlideName: tile.heading,
      coOpBrand: tile.coOpBrand,
      tileCount,
    };

    if (isModifiedClick(event)) {
      trackPromoTileClick({ data });
      return;
    }

    if (event.defaultPrevented) {
      trackPromoTileClick({ data });
      return;
    }

    event.preventDefault();
    trackPromoTileClick({
      data,
      eventCallback: () => {
        window.location.href = href;
      },
    });
  };

  return (
    <div
      className={styles.card}
      data-cta-count={tile.ctas.length}
      data-tile-count={tileCount}
    >
      <div className={styles.imageWrapper}>
        {tile.mobileImage ? (
          <PromoTilePicture
            image={tile.image}
            mobileImage={tile.mobileImage}
            sizes={sizes}
          />
        ) : (
          <Image
            src={tile.image.url}
            alt={tile.image.alt}
            fill
            sizes={sizes}
            className={styles.image}
          />
        )}
      </div>

      {hasOverlay && (
        <>
          <span className={styles.scrim} aria-hidden="true" />
          <div className={styles.content}>
            {tile.heading && (
              <Heading className={styles.heading}>{tile.heading}</Heading>
            )}
            {tile.body && <p className={styles.body}>{tile.body}</p>}
            {tile.ctas.length > 0 && (
              <div className={styles.ctaRow}>
                {tile.ctas.map((cta) => {
                  const href = localizeHref(cta.url, locale);

                  return (
                    <LinkButton
                      key={`${cta.url}-${cta.slot}`}
                      href={href}
                      variant="white"
                      className={styles.cta}
                      onClick={(event) => handleCtaClick(href, event)}
                    >
                      {cta.label}

                      {tile.heading && (
                        <span
                          className={styles.srOnly}
                        >{` ${tile.heading}`}</span>
                      )}
                    </LinkButton>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PromoTileCard;
